import React from 'react';
import { useTranslation } from 'react-i18next';
import { useStaticQuery, graphql } from 'gatsby';
import Img from 'gatsby-image/withIEPolyfill';
import BackgroundImage from 'gatsby-background-image';

import { GatsbySanityImageFluid, GatsbyImageSharpFluid_withWebp } from '../types';
import { truncateText, getReferenceUrl, convertRToSuperscript } from '../utils/utils';
import { LinkReference } from '../graphql-fragments/LinkReference';
import ReadMoreLink from './ReadMoreLink';
import GenericLink from './GenericLink';

import * as styles from './GeneralHighlightsSection.module.scss';

interface QueryData {
  backgroundImage: {
    childImageSharp: {
      fluid: GatsbyImageSharpFluid_withWebp;
    };
  };
  sanityHomepage: {
    generalHighlights: Array<{
      _key: string;
      title: string;
      readMoreUrl: string;
      readMoreReference: LinkReference;
      image: {
        asset: {
          fluid: GatsbySanityImageFluid;
        };
      };
      body: string;
    }>;
  };
}

const GeneralHighlightsSection = (): React.ReactElement => {
  const { t } = useTranslation();
  const data: QueryData = useStaticQuery(
    graphql`
      {
        backgroundImage: file(relativePath: { eq: "general-highlights-section-bg.jpg" }) {
          childImageSharp {
            fluid(maxWidth: 1920) {
              ...GatsbyImageSharpFluid_withWebp
            }
          }
        }
        sanityHomepage {
          generalHighlights {
            _key
            title
            readMoreUrl
            readMoreReference {
              ...LinkReference
            }
            image {
              asset {
                fluid(maxWidth: 760, maxHeight: 420) {
                  ...GatsbySanityImageFluid
                }
              }
            }
            body
          }
        }
      }
    `,
  );
  return (
    <BackgroundImage
      Tag="section"
      className={styles.section}
      fluid={data.backgroundImage.childImageSharp.fluid}
    >
      <div className={styles.containerBackground}>
        <div className={styles.container}>
          <h2>{t('general_highlights_section.title', 'Custom module')}</h2>
          <div className={styles.content}>
            {data.sanityHomepage?.generalHighlights?.map((highlight, i: number) => (
              <GenericLink
                noStyle
                to={
                  highlight.readMoreReference
                    ? getReferenceUrl(
                        highlight.readMoreReference.__typename,
                        highlight.readMoreReference.slug.current,
                      )
                    : highlight.readMoreUrl
                }
                key={highlight._key}
                className={
                  i !== data.sanityHomepage.generalHighlights.length - 1
                    ? styles.card
                    : styles.lastCard
                }
              >
                <Img
                  className={styles.cardImage}
                  fluid={highlight.image.asset.fluid}
                  alt={highlight.title}
                />
                <div className={styles.cardText}>
                  <span className={styles.title} title={highlight.title}>
                    {convertRToSuperscript(truncateText(highlight.title, 55))}
                  </span>
                  <p className={styles.paragraph}>
                    {convertRToSuperscript(truncateText(highlight.body, 120))}
                  </p>
                  <ReadMoreLink styleOnly>
                    {t('general_highlights_section.read_more_link', 'Read More')}
                  </ReadMoreLink>
                </div>
              </GenericLink>
            ))}
          </div>
        </div>
      </div>
    </BackgroundImage>
  );
};

export default GeneralHighlightsSection;
